

(function () {
  "use strict";

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", init);
  } else {
    init();
  }

  function init() {
    // v1–v4 fixes
    fixCartDrawerButton();
    fixLogoLinks();
    fixNewsletterButton();
    fixAriaHiddenFocusable();
    fixSlideshowFocus();
    fixImageOnlyLinks();
    fixPhotoswipeZoom();
    fixBuildYourBoxPage();
    fixGrowaveForm();
    // v5 fixes
    fixNavIconButtons();
    fixCollapsibleNavButtons();
    fixPhotoswipeNavButtons();
    fixBYBRadioLabels();
    fixEmploymentCheckboxGroup();
    fixFocusOutline();
    fixFilterGroups();
    // v6 fixes
    fixSkipNavigation();
    fixGenericLinkText();
    fixHeadingHierarchy();
    fixFormAccessibility();
    fixVideoMedia();
    watchForDynamicContent();
  }

  // ── 1. Cart drawer button ──────────────────────────────────────────────────
  // aria-controls="CartDrawer" references a nonexistent element.
  // Remove the invalid attribute; ensure the button has an accessible label.
  function fixCartDrawerButton() {
    document.querySelectorAll(".js-drawer-open-cart").forEach(function (btn) {
      var controlsId = btn.getAttribute("aria-controls");
      if (controlsId && !document.getElementById(controlsId)) {
        btn.removeAttribute("aria-controls");
      }
      if (!btn.getAttribute("aria-label") && !btn.textContent.trim()) {
        btn.setAttribute("aria-label", "Open shopping cart");
      }
    });
  }

  // ── 2. Logo links ──────────────────────────────────────────────────────────
  // Header and footer logo <a> tags contain only images/SVGs with no alt text.
  function fixLogoLinks() {
    var siteName = "Poppy & Peep";
    document.querySelectorAll(
      ".logo--has-inverted, .site-header__logo a, .header__logo a"
    ).forEach(function (el) {
      if (el.tagName !== "A") return;
      if (!el.getAttribute("aria-label") && !el.textContent.trim()) {
        el.setAttribute("aria-label", siteName + " – Home");
      }
    });
    document.querySelectorAll(".footer__logo a").forEach(function (el) {
      if (!el.getAttribute("aria-label") && !el.textContent.trim()) {
        el.setAttribute("aria-label", siteName + " – Home");
      }
    });
  }

  // ── 3. Newsletter submit button ────────────────────────────────────────────
  // The "Subscribe" label span has display:none on desktop, so the accessible
  // name computation sees only the aria-hidden SVG icon — no usable name.
  function fixNewsletterButton() {
    document.querySelectorAll(
      "#newsletter-submit-footer2, #newsletter-submit-footer, #newsletter-submit, " +
      ".newsletter__submit, [id^='newsletter-submit']"
    ).forEach(function (btn) {
      if (!btn.getAttribute("aria-label") && !btn.value) {
        btn.setAttribute("aria-label", "Subscribe to newsletter");
      }
    });
  }

  // ── 4. Elements that are aria-hidden="true" on themselves but still focusable
  function fixAriaHiddenFocusable() {
    document.querySelectorAll('[aria-hidden="true"]').forEach(function (el) {
      var tag = el.tagName;
      if (tag === "A" || tag === "BUTTON") {
        if (el.getAttribute("tabindex") !== "-1") {
          if (!el.hasAttribute("data-ppada-tabindex")) {
            el.setAttribute("data-ppada-tabindex", el.getAttribute("tabindex") || "");
          }
          el.setAttribute("tabindex", "-1");
        }
      }
    });
    var observer = new MutationObserver(function (mutations) {
      mutations.forEach(function (m) {
        if (m.attributeName !== "aria-hidden") return;
        var el = m.target;
        var tag = el.tagName;
        if (tag !== "A" && tag !== "BUTTON") return;
        if (el.getAttribute("aria-hidden") === "true") {
          if (!el.hasAttribute("data-ppada-tabindex")) {
            el.setAttribute("data-ppada-tabindex", el.getAttribute("tabindex") || "");
          }
          el.setAttribute("tabindex", "-1");
        } else {
          var saved = el.getAttribute("data-ppada-tabindex");
          if (saved !== null) {
            saved === "" ? el.removeAttribute("tabindex") : el.setAttribute("tabindex", saved);
            el.removeAttribute("data-ppada-tabindex");
          }
        }
      });
    });
    document.querySelectorAll('[aria-hidden="true"]').forEach(function (el) {
      observer.observe(el, { attributes: true, attributeFilter: ["aria-hidden"] });
    });
  }

  // ── 5. Slideshow: aria-hidden slide containers with focusable children ─────
  function fixSlideshowFocus() {
    var FOCUSABLE = "a, button, input, select, textarea, [tabindex]";

    function applyToSlide(slide) {
      var isHidden = slide.getAttribute("aria-hidden") === "true";
      slide.querySelectorAll(FOCUSABLE).forEach(function (el) {
        if (isHidden) {
          if (!el.hasAttribute("data-ppada-tabindex")) {
            el.setAttribute("data-ppada-tabindex", el.getAttribute("tabindex") || "");
          }
          el.setAttribute("tabindex", "-1");
        } else {
          var saved = el.getAttribute("data-ppada-tabindex");
          if (saved !== null) {
            saved === "" ? el.removeAttribute("tabindex") : el.setAttribute("tabindex", saved);
            el.removeAttribute("data-ppada-tabindex");
          }
        }
      });
    }

    document.querySelectorAll(".slideshow__slide").forEach(applyToSlide);

    var slideObserver = new MutationObserver(function (mutations) {
      mutations.forEach(function (m) {
        if (
          m.attributeName === "aria-hidden" &&
          m.target.classList &&
          m.target.classList.contains("slideshow__slide")
        ) {
          applyToSlide(m.target);
        }
      });
    });
    document.querySelectorAll(".slideshow__slide").forEach(function (slide) {
      slideObserver.observe(slide, { attributes: true, attributeFilter: ["aria-hidden"] });
    });
  }

  // ── 6. Image-only content links ────────────────────────────────────────────
  function fixImageOnlyLinks() {
    function labelFromHref(href) {
      var last = (href || "")
        .replace(/\/$/, "")
        .split("/")
        .pop()
        .split("?")[0]
        .split("#")[0];
      return last
        .replace(/-/g, " ")
        .replace(/\b\w/g, function (c) { return c.toUpperCase(); });
    }

    document.querySelectorAll("a").forEach(function (a) {
      if (a.getAttribute("aria-label")) return;
      if (a.textContent.trim()) return;
      var img = a.querySelector("img");
      if (!img) return;
      var imgAlt = (img.getAttribute("alt") || "").trim();
      if (imgAlt) {
        a.setAttribute("aria-label", imgAlt);
      } else {
        var label = labelFromHref(a.getAttribute("href"));
        if (label) a.setAttribute("aria-label", label);
      }
    });
  }

  // ── 7. Photoswipe zoom button (product pages) ─────────────────────────────
  function fixPhotoswipeZoom() {
    document.querySelectorAll(".js-photoswipe__zoom").forEach(function (btn) {
      if (!btn.getAttribute("aria-label")) {
        btn.setAttribute("aria-label", "Zoom image");
      }
    });
  }

  // ── 8 & 9. Build Your Box page ────────────────────────────────────────────
  function fixBuildYourBoxPage() {
    // 8. Subscription toggle
    var subscriptionCb = document.querySelector(
      '.box-subscription input[type="checkbox"][name="subscription"]'
    );
    if (subscriptionCb && !subscriptionCb.getAttribute("aria-label")) {
      subscriptionCb.setAttribute("aria-label", "Subscribe to order updates");
    }

    // 9. Color contrast — inject once
    if (document.getElementById("ppada-byb-contrast")) return;
    var style = document.createElement("style");
    style.id = "ppada-byb-contrast";
    style.textContent =
      ".box-cart-subtotal-price { color: #bf6b3d !important; }" +
      " label[for^=\"size_\"] { color: #767676 !important; }";
    document.head.appendChild(style);
  }

  // ── 10. Custom Orders — Growave contact form ───────────────────────────────
  function fixGrowaveForm() {
    document.querySelectorAll(
      ".gf_textfield, .gf_textarea, .gf_dropdown"
    ).forEach(function (el) {
      if (el.getAttribute("aria-label")) return;
      var name = el.getAttribute("name") || "";
      var match = name.match(/contact\[(.+)\]/);
      if (match && match[1]) {
        el.setAttribute("aria-label", match[1]);
      }
    });
  }

  // ── 11. Nav icon buttons ───────────────────────────────────────────────────
  // Hamburger, search open/close, drawer close, modal close, tooltip close,
  // and mobile Vimeo play trigger — all contain only SVG icons with no accessible
  // name. NOTE (v7): do NOT guard with !btn.textContent.trim() — SVG inline
  // <style> blocks leak CSS text into textContent, which would prevent the label
  // from being applied even when the button has no visible text.
  function fixNavIconButtons() {
    var labelMap = [
      { selector: ".site-nav__link--icon.js-drawer-open",       label: "Open navigation menu" },
      { selector: ".btn--search",                                 label: "Search" },
      { selector: ".btn--close-search",                           label: "Close search" },
      { selector: ".drawer__close-button.js-drawer-close",        label: "Close menu" },
      // Generic drawer close (any drawer not covered above)
      { selector: ".drawer__close-button:not(.js-drawer-close)", label: "Close" },
      { selector: ".modal__close.js-modal-close",                 label: "Close" },
      { selector: ".tool-tip__close",                             label: "Close" },
      // Mobile Vimeo play button (fix 25 — browser QA)
      { selector: ".vimeo-mobile-trigger",                        label: "Play video" }
    ];

    labelMap.forEach(function (entry) {
      document.querySelectorAll(entry.selector).forEach(function (btn) {
        // Check aria-label only — NOT textContent, because SVG inline <style>
        // blocks pollute textContent with CSS strings even on icon-only buttons.
        if (!btn.getAttribute("aria-label")) {
          btn.setAttribute("aria-label", entry.label);
        }
      });
    });
  }

  // ── 24. Collapsible header nav trigger buttons ─────────────────────────────
  // Some Impulse nav sections render a <button class="collapsible-trigger
  // collapsible--auto-height"> that contains only an SVG chevron — no text,
  // no aria-label. The button's aria-controls attribute points to the collapsible
  // panel that lists the sub-navigation links. Derive the label from the first
  // link inside that panel so AT users know what category they're expanding.
  function fixCollapsibleNavButtons() {
    document.querySelectorAll(
      "button.collapsible-trigger.collapsible--auto-height"
    ).forEach(function (btn) {
      if (btn.getAttribute("aria-label")) return;     // already labeled

      // innerText is reliable here (no SVG style pollution on these buttons)
      var visibleText = (btn.innerText || "").trim();
      if (visibleText) return;                         // has real visible text — skip

      // Derive label from the controlled panel's first link
      var controlsId = btn.getAttribute("aria-controls");
      var panel = controlsId ? document.getElementById(controlsId) : null;
      if (!panel) {
        // Fallback: next sibling collapsible-content
        panel = btn.nextElementSibling;
        if (panel && !panel.classList.contains("collapsible-content")) panel = null;
      }

      var firstLink = panel ? panel.querySelector("a") : null;
      if (firstLink && firstLink.textContent.trim()) {
        // e.g. "Shop" → "Expand Shop navigation"
        var label = "Expand " + firstLink.textContent.trim() + " navigation";
        btn.setAttribute("aria-label", label);
      } else if (panel && panel.id) {
        // Last resort: use the panel id (e.g. "Linklist-collections-shop1")
        var readable = panel.id.replace(/^Linklist-/, "").replace(/-\d+$/, "").replace(/-/g, " ");
        btn.setAttribute("aria-label", "Expand " + readable + " navigation");
      }
    });
  }

  // ── 12. Photoswipe prev / next / close nav buttons ────────────────────────
  // These buttons exist in the DOM when the gallery is open (injected by
  // Photoswipe). Run on init and again via MutationObserver when gallery opens.
  function fixPhotoswipeNavButtons() {
    var pswpMap = [
      { selector: ".pswp__button--arrow--left",  label: "Previous image" },
      { selector: ".pswp__button--arrow--right", label: "Next image"     },
      { selector: ".pswp__button--close",        label: "Close gallery"  }
    ];

    pswpMap.forEach(function (entry) {
      document.querySelectorAll(entry.selector).forEach(function (btn) {
        if (!btn.getAttribute("aria-label")) {
          btn.setAttribute("aria-label", entry.label);
        }
      });
    });
  }

  // ── 13. Build Your Box — radio button unit context ─────────────────────────
  // Radio inputs for jar size have values "6", "12", "24" with no unit context.
  // Wrap each with a visually-hidden span so AT reads "6 chocolates", etc.
  function fixBYBRadioLabels() {
    var unitMap = { "6": "6 chocolates", "12": "12 chocolates", "24": "24 chocolates" };

    document.querySelectorAll('input[type="radio"][name="jar-type"]').forEach(function (input) {
      if (input.getAttribute("data-ppada-fixed")) return;
      var val = input.value;
      var fullLabel = unitMap[val];
      if (fullLabel) {
        input.setAttribute("aria-label", fullLabel);
        input.setAttribute("data-ppada-fixed", "1");
      }
    });
  }

  // ── 14. Employment form checkbox group ─────────────────────────────────────
  // "Yes" and "No" checkboxes for employment status lack group context.
  // Screen readers read "Yes" / "No" with no indication of what they answer.
  function fixEmploymentCheckboxGroup() {
    var employedMap = [
      { name: "Employed - Yes", label: "Currently employed: Yes" },
      { name: "Employed - No",  label: "Currently employed: No"  }
    ];

    employedMap.forEach(function (entry) {
      document.querySelectorAll('input[name="' + entry.name + '"]').forEach(function (input) {
        if (!input.getAttribute("aria-label")) {
          input.setAttribute("aria-label", entry.label);
        }
      });
    });
  }

  // ── 15. Focus outline — keyboard focus visibility ──────────────────────────
  // Impulse theme suppresses focus outlines unless .tab-outline is on <html>.
  // Inject :focus-visible styles so keyboard users always see a focus ring,
  // independent of the theme's tab-detection JavaScript.
  function fixFocusOutline() {
    if (document.getElementById("ppada-focus-outline")) return;
    var style = document.createElement("style");
    style.id = "ppada-focus-outline";
    style.textContent = [
      // Restore focus ring for keyboard navigation using :focus-visible.
      // This overrides the theme's blanket `outline: none` rule.
      ":focus-visible {",
      "  outline: 3px solid #c0665a !important;",
      "  outline-offset: 2px !important;",
      "}",
      // Buttons and links: use box-shadow as fallback where outline clips border-radius
      "button:focus-visible,",
      "a:focus-visible,",
      "[role='button']:focus-visible {",
      "  outline: 3px solid #c0665a !important;",
      "  outline-offset: 2px !important;",
      "  box-shadow: 0 0 0 3px rgba(192, 102, 90, 0.4) !important;",
      "}",
      // Suppress focus ring for mouse/touch clicks (non-keyboard)
      ":focus:not(:focus-visible) {",
      "  outline: none !important;",
      "  box-shadow: none !important;",
      "}"
    ].join("\n");
    document.head.appendChild(style);
  }

  // ── 16. Build Your Box cart — dynamically added item buttons ──────────────
  // .box-cart-item elements are injected after the user adds chocolates.
  // Each item has quantity +/- and remove (x) buttons with no accessible name.
  // Called from MutationObserver when .box-cart-item elements are added.
  function fixBYBCartButtons() {
    document.querySelectorAll(".box-cart-item").forEach(function (item) {
      // Derive chocolate name from the item title element for better AT context
      var titleEl = item.querySelector(".box-cart-item-title, .box-cart-item__title");
      var chocolateName = titleEl ? titleEl.textContent.trim() : "item";

      // Quantity decrease / increase buttons
      var decreaseBtn = item.querySelector(".box-cart-item-minus, [data-action='decrease'], .js-qty-minus");
      var increaseBtn = item.querySelector(".box-cart-item-plus, [data-action='increase'], .js-qty-plus");
      var removeBtn   = item.querySelector(".box-cart-item-remove, [data-action='remove'], .js-remove-item");

      if (decreaseBtn && !decreaseBtn.getAttribute("aria-label")) {
        decreaseBtn.setAttribute("aria-label", "Decrease quantity of " + chocolateName);
      }
      if (increaseBtn && !increaseBtn.getAttribute("aria-label")) {
        increaseBtn.setAttribute("aria-label", "Increase quantity of " + chocolateName);
      }
      if (removeBtn && !removeBtn.getAttribute("aria-label")) {
        removeBtn.setAttribute("aria-label", "Remove " + chocolateName + " from box");
      }

      // Generic icon-only buttons inside cart items not matched above
      item.querySelectorAll("button").forEach(function (btn) {
        if (btn.getAttribute("aria-label")) return;
        if (btn.textContent.trim()) return;
        // Infer label from class names
        var cls = btn.className || "";
        if (/minus|decrease|less/.test(cls)) {
          btn.setAttribute("aria-label", "Decrease quantity of " + chocolateName);
        } else if (/plus|increase|add/.test(cls)) {
          btn.setAttribute("aria-label", "Increase quantity of " + chocolateName);
        } else if (/remove|delete|close/.test(cls)) {
          btn.setAttribute("aria-label", "Remove " + chocolateName + " from box");
        }
      });
    });
  }

  // ── 17. Filter checkbox groups ─────────────────────────────────────────────
  // Impulse theme renders each filter category inside a .collection-sidebar__group
  // wrapper. The category label is a .collapsible-trigger-btn button (which
  // already has visible text like "Chocolate"). The checkboxes live inside a
  // sibling .collapsible-content div. We wrap the content panel with
  // role="group" + aria-labelledby pointing to the trigger button so screen
  // readers announce "Chocolate group" before reading the checkbox list.
  //
  // v7 note: previous version targeted .filter-group / .facet-group /
  // [data-filter-group] — none of these selectors exist in Impulse v7.4.1.
  function fixFilterGroups() {
    document.querySelectorAll(".collection-sidebar__group").forEach(function (group) {
      // The collapsible trigger button — already has visible text (category name)
      var triggerBtn = group.querySelector(
        ".collapsible-trigger-btn, button.collapsible-trigger"
      );
      // The content panel that holds the checkboxes
      var contentPanel = group.querySelector(".collapsible-content");

      if (!triggerBtn || !contentPanel) return;
      if (contentPanel.getAttribute("role") === "group") return;  // already fixed

      // Ensure the trigger button has a stable id to reference
      if (!triggerBtn.id) {
        // Prefer aria-controls value (e.g. "CollectionSidebar-1-filter-chocolate")
        // so the id stays predictable across re-runs
        var controlsId = triggerBtn.getAttribute("aria-controls");
        triggerBtn.id = controlsId
          ? "ppada-trigger-" + controlsId
          : "ppada-trigger-" + Math.random().toString(36).slice(2, 8);
      }

      contentPanel.setAttribute("role", "group");
      contentPanel.setAttribute("aria-labelledby", triggerBtn.id);
    });

    // Fallback for themes/pages that DO use older facet selector patterns
    var legacySelectors = [".filter-group", ".facet-group", "[data-filter-group]"];
    legacySelectors.forEach(function (sel) {
      document.querySelectorAll(sel).forEach(function (group) {
        if (group.getAttribute("role") === "group") return;
        var heading = group.querySelector("h3, h4, .filter-group__heading, .facet__title, p.filter__label");
        if (!heading) return;
        if (!heading.id) {
          heading.id = "ppada-fg-" + Math.random().toString(36).slice(2, 8);
        }
        group.setAttribute("role", "group");
        group.setAttribute("aria-labelledby", heading.id);
      });
    });
  }

  // ── 18. Skip to main content link ─────────────────────────────────────────
  // Inject a visually-hidden "Skip to main content" link as the very first
  // focusable element on the page. It becomes visible on :focus so keyboard
  // users can bypass the header nav on every page load (WCAG 2.4.1).
  function fixSkipNavigation() {
    if (document.getElementById("ppada-skip-link")) return;

    // Inject CSS for the skip link (once)
    if (!document.getElementById("ppada-skip-link-style")) {
      var style = document.createElement("style");
      style.id = "ppada-skip-link-style";
      style.textContent = [
        "#ppada-skip-link {",
        "  position: absolute;",
        "  top: -9999px;",
        "  left: 1rem;",
        "  z-index: 99999;",
        "  background: #fff;",
        "  color: #c0665a;",
        "  font-size: 1rem;",
        "  font-weight: 600;",
        "  padding: 0.75rem 1.25rem;",
        "  border: 2px solid #c0665a;",
        "  border-radius: 4px;",
        "  text-decoration: none;",
        "  outline: none;",
        "}",
        "#ppada-skip-link:focus-visible {",
        "  top: 1rem;",
        "}"
      ].join("\n");
      document.head.appendChild(style);
    }

    // Find (or create) the main content landmark target
    var mainEl = document.querySelector("main, [role='main'], #MainContent, #shopify-section-header + *");
    if (mainEl && !mainEl.id) {
      mainEl.id = "ppada-main-content";
    }
    var targetId = mainEl ? mainEl.id : "ppada-main-content";

    var link = document.createElement("a");
    link.id = "ppada-skip-link";
    link.href = "#" + targetId;
    link.textContent = "Skip to main content";

    // Insert as the very first child of <body>
    document.body.insertBefore(link, document.body.firstChild);
  }

  // ── 19. Generic link text — contextual accessible names ───────────────────
  // Short CTAs like "Shop Now" and "Get Started" are ambiguous when a screen
  // reader user navigates by links. Derive context from the nearest section
  // heading and append it to the aria-label.
  function fixGenericLinkText() {
    var GENERIC_LABELS = [
      "shop now", "shop all", "get started", "learn more",
      "read more", "view all", "see all", "explore", "discover",
      "click here", "here"
    ];

    function getNearestHeading(el) {
      // Walk up the DOM looking for a heading sibling or ancestor heading
      var node = el;
      var maxDepth = 6;
      while (node && maxDepth-- > 0) {
        // Look at previous siblings
        var sib = node.previousElementSibling;
        while (sib) {
          if (/^H[1-6]$/.test(sib.tagName)) {
            return sib.textContent.trim();
          }
          // Check last heading inside sib (e.g. a wrapper div)
          var inner = sib.querySelector("h1,h2,h3,h4,h5,h6");
          if (inner) return inner.textContent.trim();
          sib = sib.previousElementSibling;
        }
        node = node.parentElement;
      }
      return "";
    }

    document.querySelectorAll("a").forEach(function (a) {
      if (a.getAttribute("aria-label")) return;          // already labeled
      var text = a.textContent.trim().toLowerCase();
      if (!GENERIC_LABELS.includes(text)) return;

      var visible = a.textContent.trim();
      var heading = getNearestHeading(a);

      var label = heading
        ? visible + " – " + heading
        : visible + " – Poppy & Peep";

      a.setAttribute("aria-label", label);
      a.setAttribute("data-ppada-generic-link", "1");
    });
  }

  // ── 20. Heading hierarchy ──────────────────────────────────────────────────
  // Impulse theme templates sometimes skip heading levels (H2 → H4) and may
  // produce multiple H1 elements. Setting aria-level on native heading elements
  // overrides the implicit level announced by screen readers while preserving
  // the visual styling tied to the original tag.
  function fixHeadingHierarchy() {
    var headings = Array.from(document.querySelectorAll("h1,h2,h3,h4,h5,h6"));
    if (!headings.length) return;

    var lastLevel = 0;
    var h1Count   = 0;

    headings.forEach(function (h) {
      // Skip elements already managed by this patch
      if (h.getAttribute("data-ppada-heading")) return;

      var nativeLevel = parseInt(h.tagName[1], 10);

      // Multiple H1 elements: promote 2nd+ to level 2
      if (nativeLevel === 1) {
        h1Count++;
        if (h1Count > 1) {
          h.setAttribute("aria-level", "2");
          h.setAttribute("data-ppada-heading", "1");
          lastLevel = 2;
        } else {
          lastLevel = 1;
        }
        return;
      }

      // Fill in skipped levels (e.g. last was 2, current is 4 → set to 3)
      if (lastLevel > 0 && nativeLevel > lastLevel + 1) {
        var corrected = lastLevel + 1;
        h.setAttribute("aria-level", String(corrected));
        h.setAttribute("data-ppada-heading", "1");
        lastLevel = corrected;
      } else {
        lastLevel = nativeLevel;
      }
    });
  }

  // ── 21. Form accessibility — required fields and error handling ─────────────
  // Custom Orders (Growave) and Employment forms lack programmatic required-field
  // marking and do not associate error messages to their inputs.
  // This fix:
  //   a) marks fields with [required] or a visual asterisk as aria-required="true"
  //   b) associates existing error elements to their input via aria-describedby
  //   c) adds an aria-live error summary region so dynamic errors are announced
  function fixFormAccessibility() {
    // (a) Mark required fields
    document.querySelectorAll(
      "input[required], select[required], textarea[required]"
    ).forEach(function (field) {
      if (!field.getAttribute("aria-required")) {
        field.setAttribute("aria-required", "true");
      }
    });

    // Also mark fields whose label or wrapper contains a visual * indicator
    document.querySelectorAll("label").forEach(function (label) {
      if (!label.textContent.includes("*")) return;
      var forId = label.getAttribute("for");
      var field = forId
        ? document.getElementById(forId)
        : label.querySelector("input, select, textarea");
      if (field && !field.getAttribute("aria-required")) {
        field.setAttribute("aria-required", "true");
      }
    });

    // (b) Associate existing error elements
    document.querySelectorAll(
      ".gf_error, .form__message--error, .field__message--error, [class*='error-message']"
    ).forEach(function (errEl) {
      if (!errEl.id) {
        errEl.id = "ppada-err-" + Math.random().toString(36).slice(2, 8);
      }
      // Find the nearest input to associate
      var field = errEl.previousElementSibling || errEl.parentElement.querySelector("input, select, textarea");
      if (!field) return;
      var existing = field.getAttribute("aria-describedby") || "";
      if (existing.indexOf(errEl.id) === -1) {
        field.setAttribute("aria-describedby", (existing + " " + errEl.id).trim());
      }
      field.setAttribute("aria-invalid", "true");
    });

    // (c) Inject a live region for dynamic form errors (if not already present)
    if (!document.getElementById("ppada-form-status")) {
      var live = document.createElement("div");
      live.id = "ppada-form-status";
      live.setAttribute("role", "status");
      live.setAttribute("aria-live", "polite");
      live.setAttribute("aria-atomic", "true");
      // Visually hidden but readable by AT
      live.style.cssText = "position:absolute;width:1px;height:1px;overflow:hidden;clip:rect(0,0,0,0);white-space:nowrap;";
      document.body.appendChild(live);
    }

    // Watch for dynamically-injected error messages and announce them
    var errObserver = new MutationObserver(function (mutations) {
      mutations.forEach(function (m) {
        m.addedNodes.forEach(function (node) {
          if (node.nodeType !== 1) return;
          var isErr = node.classList && (
            node.classList.contains("gf_error") ||
            node.classList.contains("form__message--error") ||
            node.className.indexOf("error") !== -1
          );
          if (isErr) {
            var statusEl = document.getElementById("ppada-form-status");
            if (statusEl) statusEl.textContent = node.textContent.trim();
          }
        });
      });
    });
    document.querySelectorAll("form").forEach(function (form) {
      errObserver.observe(form, { childList: true, subtree: true });
    });
  }

  // ── 22. Video and media ─────────────────────────────────────────────────────
  // Homepage may include <video> elements:
  //   - Ensure controls are present so keyboard users can operate them
  //   - Prevent autoplay with audio (mute any autoplaying video)
  //   - Give each video element an accessible title via aria-label
  function fixVideoMedia() {
    document.querySelectorAll("video").forEach(function (video) {
      // Accessible title from data attribute, source filename, or generic fallback
      if (!video.getAttribute("aria-label")) {
        var title = video.getAttribute("data-video-title") ||
                    video.getAttribute("title") ||
                    "Poppy & Peep video";
        video.setAttribute("aria-label", title);
      }

      // Mute autoplay videos to prevent unexpected audio
      if (video.hasAttribute("autoplay") && !video.muted) {
        video.muted = true;
      }

      // Ensure controls are present for non-decorative video
      // Exception: purely decorative looping background videos (muted + loop + no controls intended)
      var isDecorative = video.hasAttribute("loop") && video.muted && !video.hasAttribute("controls");
      if (!isDecorative && !video.hasAttribute("controls")) {
        video.setAttribute("controls", "");
      }

      // Decorative looping background video — hide from AT
      if (isDecorative && !video.getAttribute("aria-hidden")) {
        video.setAttribute("aria-hidden", "true");
      }
    });

    // Accessible iframes (YouTube/Vimeo embeds)
    document.querySelectorAll("iframe[src*='youtube'], iframe[src*='vimeo'], iframe[src*='player']").forEach(function (iframe) {
      if (!iframe.getAttribute("title")) {
        iframe.setAttribute("title", "Poppy & Peep video");
      }
    });
  }

  // ── MutationObserver for dynamic content ───────────────────────────────────
  function watchForDynamicContent() {
    var timer = null;

    // Debounced general re-run for most fixes
    var bodyObserver = new MutationObserver(function () {
      clearTimeout(timer);
      timer = setTimeout(function () {
        fixCartDrawerButton();
        fixLogoLinks();
        fixNewsletterButton();
        fixAriaHiddenFocusable();
        fixImageOnlyLinks();
        fixPhotoswipeZoom();
        fixBuildYourBoxPage();
        fixGrowaveForm();
        fixNavIconButtons();
        fixCollapsibleNavButtons();
        fixPhotoswipeNavButtons();   // photoswipe DOM injected on gallery open
        fixBYBRadioLabels();
        fixEmploymentCheckboxGroup();
        fixBYBCartButtons();         // cart items added when user selects chocolates
        fixFilterGroups();
        fixGenericLinkText();
        fixHeadingHierarchy();
        fixFormAccessibility();
        fixVideoMedia();
      }, 300);
    });
    bodyObserver.observe(document.body, { childList: true, subtree: true });
  }

})();